import { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import VideoPlayer from '../components/VideoPlayer';
import api from '../services/api';
import './CoursePlayer.css';

function CoursePlayer() {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [videos, setVideos] = useState([]);
  const [currentVideo, setCurrentVideo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [videoProgress, setVideoProgress] = useState({});
  const [courseProgress, setCourseProgress] = useState(0);
  const updateTimeoutRef = useRef(null);
  const videoRef = useRef(null);
  const user = JSON.parse(localStorage.getItem('user_data') || 'null');

  useEffect(() => {
    if (!user || !localStorage.getItem('auth_token')) { navigate('/treinamentos'); return; }
    loadCourseVideos();
  }, [courseId]);

  const loadCourseVideos = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/courses/${courseId}/videos`, true);
      if (response.success) {
        setCourse(response.data.course);
        setVideos(response.data.videos);
        if (response.data.videos.length > 0) setCurrentVideo(response.data.videos[0]);
        const savedProgress = localStorage.getItem(`course_progress_${courseId}`);
        if (savedProgress) {
          const progress = JSON.parse(savedProgress);
          setVideoProgress(progress.videos || {});
          setCourseProgress(progress.overall || 0);
        }
      }
    } catch (err) {
      setError(err.message || 'Erro ao carregar vídeos do curso');
    } finally {
      setLoading(false);
    }
  };

  const handleVideoSelect = (video) => {
    if (currentVideo && videoRef.current) {
      localStorage.setItem(
        `video_time_${courseId}_${currentVideo.key}`,
        videoRef.current.currentTime.toString()
      );
    }
    setCurrentVideo(video);
  };

  const handleVideoProgress = (videoKey, progress) => {
    const newVideoProgress = { ...videoProgress, [videoKey]: progress };
    setVideoProgress(newVideoProgress);
    const totalVideos = videos.length;
    const completedVideos = Object.values(newVideoProgress).filter(p => p >= 90).length;
    const overall = Math.round((completedVideos / totalVideos) * 100);
    setCourseProgress(overall);
    localStorage.setItem(`course_progress_${courseId}`, JSON.stringify({ videos: newVideoProgress, overall }));
    if (updateTimeoutRef.current) clearTimeout(updateTimeoutRef.current);
    updateTimeoutRef.current = setTimeout(() => updateCourseProgress(overall), 5000);
  };

  const updateCourseProgress = async (progress) => {
    try {
      await api.put(`/enrollments/${courseId}/progress`, { progress }, true);
    } catch (err) {
      console.error('Error updating progress:', err);
    }
  };

  const handleVideoEnd = () => {
    const currentIndex = videos.findIndex(v => v.key === currentVideo.key);
    if (currentIndex < videos.length - 1) setCurrentVideo(videos[currentIndex + 1]);
  };

  const handleLogout = () => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user_data');
    navigate('/treinamentos');
  };

  if (loading) {
    return (
      <div className="course-player-loading">
        <div className="cp-spinner"/>
        <p>Carregando curso...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="course-player-error">
        <h2>Ocorreu um erro</h2>
        <p>{error}</p>
        <button className="cp-error-btn" onClick={() => navigate('/treinamentos/meus-cursos')}>
          Voltar aos Meus Cursos
        </button>
      </div>
    );
  }

  return (
    <div className="course-player">
      {/* Header */}
      <header className="player-header">
        <div className="player-header-bar">
          <button className="player-back-btn" onClick={() => navigate('/treinamentos/meus-cursos')}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5M12 19l-7-7 7-7"/>
            </svg>
            Voltar
          </button>

          <span className="player-course-title">{course?.title}</span>

          <div className="player-header-actions">
            <span className="player-user-name">Olá, {user?.name?.split(' ')[0]}</span>
            <button className="player-header-btn" onClick={() => navigate('/treinamentos/meus-cursos')}>
              Meus Cursos
            </button>
            <button className="player-logout-btn" onClick={handleLogout} title="Sair">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                <polyline points="16 17 21 12 16 7"/>
                <line x1="21" y1="12" x2="9" y2="12"/>
              </svg>
            </button>
          </div>
        </div>

        {/* Progress bar */}
        <div className="course-progress-bar">
          <div className="progress-info">
            <span>Progresso do Curso</span>
            <span className="progress-percentage">{courseProgress}%</span>
          </div>
          <div className="progress-track">
            <div className="progress-fill" style={{ width: `${courseProgress}%` }}/>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="player-content">
        <div className="player-grid">
          {/* Video */}
          <div className="video-section">
            <VideoPlayer
              ref={videoRef}
              video={currentVideo}
              courseId={courseId}
              onVideoEnd={handleVideoEnd}
              onProgress={(progress) => handleVideoProgress(currentVideo.key, progress)}
            />
            {currentVideo && (
              <div className="video-info">
                <h2>{currentVideo.title}</h2>
                <p>{course?.description}</p>
              </div>
            )}
          </div>

          {/* Playlist */}
          <div className="playlist-section">
            <h3>Conteúdo do Curso</h3>
            <div className="playlist">
              {videos.map((video, index) => {
                const progress = videoProgress[video.key] || 0;
                const isCompleted = progress >= 90;
                const isActive = currentVideo?.key === video.key;

                return (
                  <div
                    key={video.key}
                    className={`playlist-item${isActive ? ' active' : ''}${isCompleted ? ' completed' : ''}`}
                    onClick={() => handleVideoSelect(video)}
                  >
                    <div className="playlist-item-number">{index + 1}</div>
                    <div className="playlist-item-info">
                      <h4>{video.title}</h4>
                      {progress > 0 && (
                        <div className="playlist-item-meta">
                          <span className="playlist-item-progress">{Math.round(progress)}%</span>
                        </div>
                      )}
                    </div>
                    {isCompleted && (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#4ade80" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12"/>
                      </svg>
                    )}
                    {isActive && !isCompleted && (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="#a78bfa">
                        <polygon points="5 3 19 12 5 21 5 3"/>
                      </svg>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CoursePlayer;
